/**
 * The one door to gsap.
 *
 * Owns: the gsap instance and three helpers that already know about reduced
 * motion, so a call site never checks it. Must not: hold animation state, or
 * be used for the tarp (that is Web Animations on purpose, because gsap's lag
 * smoothing would stretch the tarp across the Three scene's heavy first
 * frame; see Tarp.vue).
 *
 * Under reduced motion every helper finishes at once: `enter` leaves the
 * element as it is, `leave` calls `done` synchronously, `tweenNumber` hands
 * over the final value.
 */
import gsap from 'gsap';

import { reducedMotion } from '@/engine/motion';

export { gsap };

/** What an element returns to once a tween has played: the tween's own inline styles go. */
const CLEAR = 'opacity,transform';

/** Animate `el` *from* `vars` to its resting state. `done` fires when it has settled. */
export function enter(el: Element, vars: gsap.TweenVars, done?: () => void): void {
  if (reducedMotion()) {
    done?.();
    return;
  }
  gsap.from(el, { clearProps: CLEAR, ...vars, onComplete: done });
}

/** Animate `el` *to* `vars`, then `done` — the moment a Vue <Transition> may remove it. */
export function leave(el: Element, vars: gsap.TweenVars, done: () => void): void {
  if (reducedMotion()) {
    done();
    return;
  }
  gsap.to(el, { ...vars, onComplete: done });
}

/** Count `from` → `to`, calling `onUpdate` with the current value. Returns a cancel. */
export function tweenNumber(from: number, to: number, onUpdate: (value: number) => void, seconds: number): () => void {
  if (reducedMotion()) {
    onUpdate(to);
    return () => undefined;
  }
  const state = { v: from };
  const tween = gsap.to(state, { v: to, duration: seconds, ease: 'power2.out', onUpdate: () => onUpdate(state.v) });
  return () => {
    tween.kill();
  };
}
